import {
  FollowUpOutcome,
  FollowUpType,
  fromBackendFollowUpOutcome,
  fromBackendFollowUpType,
  toBackendFollowUpType,
} from '@/constants/followUp'
import { DayjsProperty } from '@/lib/jackson'
import { Dayjs } from 'dayjs'
import { JsonProperty, Serializable, deserialize } from 'ts-jackson'
import { Fromable, PaginationResponse } from '../model'

export interface LeadFollowUpSummary {
  overdue: number
  dueToday: number
  dueWithin7: number
}

export interface LeadListResponse extends PaginationResponse<Lead> {
  followUpSummary?: LeadFollowUpSummary
}

@Serializable()
export class LeadOption extends Fromable {
  @JsonProperty('slug')
  slug!: string

  @JsonProperty('label')
  label!: string
}

@Serializable()
export class OwnerUser extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname?: string

  @JsonProperty('gender')
  gender?: string

  @DayjsProperty()
  dateOfBirth?: Dayjs
}

@Serializable()
export class PackageType extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('nameTh')
  nameTh!: string

  @JsonProperty('nameEn')
  nameEn!: string
}

@Serializable()
export class PackageInterested extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('name')
  name!: string

  @JsonProperty({ path: 'packageType', type: PackageType })
  packageType!: PackageType

  @JsonProperty('price')
  price!: number
}

@Serializable()
export class ReferralMember extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('firstName')
  firstName!: string

  @JsonProperty('lastName')
  lastName!: string

  @JsonProperty('nickname')
  nickname!: string
}

@Serializable()
export class FollowUpStaffUser extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('firstName')
  firstName?: string

  @JsonProperty('lastName')
  lastName?: string

  @JsonProperty('nickname')
  nickname?: string
}

@Serializable()
export class LeadFollowUp extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('leadId')
  leadId!: number

  @JsonProperty('staffId')
  staffId!: number

  @JsonProperty({ path: 'staffUser', type: FollowUpStaffUser })
  staffUser?: FollowUpStaffUser

  @JsonProperty('note')
  note!: string

  @JsonProperty('result')
  result!: string

  @JsonProperty({
    path: 'type',
    deserialize: (value: unknown) =>
      fromBackendFollowUpType(typeof value === 'string' ? value : null),
  })
  type?: FollowUpType | null

  @JsonProperty({
    path: 'outcome',
    deserialize: (value: unknown) =>
      fromBackendFollowUpOutcome(typeof value === 'string' ? value : null),
  })
  outcome?: FollowUpOutcome | null

  /** The appointment (วันนัดติดตาม). */
  @DayjsProperty()
  nextActionAt!: Dayjs

  /** The day the follow-up actually happened; unset while pending. */
  @DayjsProperty()
  occuredAt?: Dayjs

  @DayjsProperty()
  createdAt!: Dayjs

  /** Audit: when / by whom the result was recorded. */
  @DayjsProperty()
  resultRecordedAt?: Dayjs

  @JsonProperty({ path: 'resultStaffUser', type: FollowUpStaffUser })
  resultStaffUser?: FollowUpStaffUser

  public serializeToCreate() {
    return {
      followUpDate: this.nextActionAt?.toISOString(),
      note: this.note,
      type: toBackendFollowUpType(this.type),
    }
  }
}

/** Step 2 of a follow-up (QTY-2095): the result, dated by the day it happened. */
export interface RecordFollowUpResultPayload {
  followedUpAt: string
  outcome: FollowUpOutcome
  result: string
  note?: string
}

@Serializable()
export class LeadReferral extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('leadId')
  leadId!: number

  @JsonProperty('referralMemberId')
  referralMemberId!: number

  @JsonProperty('isActive')
  isActive!: boolean

  @JsonProperty('referralMember')
  referralMember!: ReferralMember
}

@Serializable()
export class LeadStatusTransition extends Fromable {
  @JsonProperty('fromStatus')
  fromStatus!: string

  @JsonProperty('toStatus')
  toStatus!: string

  @JsonProperty('reason')
  reason!: string

  @DayjsProperty()
  createdAt!: Dayjs
}

@Serializable()
export class Lead extends Fromable {
  @JsonProperty('id')
  id!: number

  @JsonProperty('saleId')
  saleId!: number

  @JsonProperty('ownerUserId')
  ownerUserId!: number

  @JsonProperty('memberId')
  memberId?: number

  @JsonProperty('source')
  source!: string

  @JsonProperty('sourceChannel')
  sourceChannel!: string

  @JsonProperty('sourceChannelOther')
  sourceChannelOther!: string

  @JsonProperty('campaignId')
  campaignId?: string | null

  /** "CODE Name", already resolved by the API — the CRM never labels it itself. */
  @JsonProperty('campaignName')
  campaignName?: string | null

  /**
   * When the lead reached MEMBER or CLOSE_LOST. Distinct from `closedAt`, which
   * the API reuses to store the *expected* close date.
   */
  @DayjsProperty()
  closedDate?: Dayjs

  @JsonProperty('utmSource')
  utmSource?: string | null

  @JsonProperty('utmMedium')
  utmMedium?: string | null

  @JsonProperty('utmCampaign')
  utmCampaign?: string | null

  @JsonProperty('leadType')
  leadType?: string | null

  @JsonProperty('branchId')
  branchId!: number

  @JsonProperty('code')
  code!: string

  @JsonProperty('contactName')
  contactName!: string

  @JsonProperty('contactPhone')
  contactPhone!: string

  @JsonProperty('contactEmail')
  contactEmail!: string

  @JsonProperty('contactLineId')
  contactLineId?: string | null

  @JsonProperty('contactFacebook')
  contactFacebook?: string | null

  @JsonProperty('otherContact')
  otherContact?: string | null

  @JsonProperty('contactNickname')
  contactNickname!: string

  @JsonProperty('contactGender')
  contactGender!: string

  @JsonProperty('citizenId')
  citizenId!: string

  @DayjsProperty()
  dateOfBirth!: Dayjs

  @JsonProperty('interestedService')
  interestedService!: string[]

  @JsonProperty('interestedPackages')
  interestedPackages!: number[]

  @JsonProperty({
    path: 'leadStatusTransition',
    elementType: LeadStatusTransition,
    deserialize: (value: unknown): LeadStatusTransition[] => {
      if (!Array.isArray(value)) return []
      return value.map((item: Record<string, unknown>) => deserialize(item, LeadStatusTransition))
    },
  })
  leadStatusTransition!: LeadStatusTransition[]

  @JsonProperty('status')
  status!: string

  @JsonProperty('opportunity')
  opportunity!: number

  @JsonProperty('note')
  note!: string

  @JsonProperty('noteByUserId')
  noteByUserId!: number

  @DayjsProperty()
  notedAt!: Dayjs

  @DayjsProperty()
  closedAt!: Dayjs

  @JsonProperty('closeValueAmount')
  closeValueAmount!: number

  @JsonProperty('closeValueCurrency')
  closeValueCurrency!: string

  @DayjsProperty()
  createdAt!: Dayjs

  @DayjsProperty()
  updatedAt!: Dayjs

  @DayjsProperty()
  deletedAt!: Dayjs

  @JsonProperty('ownerUser')
  ownerUser!: OwnerUser

  @JsonProperty({
    path: 'leadReferral',
    elementType: LeadReferral,
    deserialize: (value: unknown): LeadReferral[] => {
      if (!Array.isArray(value)) return []
      return value.map((item: Record<string, unknown>) => deserialize(item, LeadReferral))
    },
  })
  leadReferral!: LeadReferral[]

  /** Nearest still-pending appointment (QTY-2096); unset when none is open. */
  @DayjsProperty()
  nextFollowUpAt?: Dayjs

  /** Day of the latest recorded follow-up; unset when none was recorded yet. */
  @DayjsProperty()
  lastFollowedUpAt?: Dayjs

  @JsonProperty({
    path: 'leadFollowUp',
    elementType: LeadFollowUp,
    deserialize: (value: unknown): LeadFollowUp[] => {
      if (!Array.isArray(value)) return []
      return value.map((item: Record<string, unknown>) => deserialize(item, LeadFollowUp))
    },
  })
  leadFollowUp!: LeadFollowUp[]

  @JsonProperty({
    path: 'packageInterested',
    elementType: PackageInterested,
    deserialize: (value: unknown): (string | PackageInterested)[] => {
      if (!Array.isArray(value)) return []
      return value.map((item: unknown) => {
        if (typeof item === 'string') {
          return item
        }
        return deserialize(item as Record<string, unknown>, PackageInterested)
      })
    },
  })
  packageInterested!: string[] | PackageInterested[]

  public serializeToCreate() {
    return {
      firstName: this.ownerUser?.firstName,
      lastName: this.ownerUser?.lastName,
      nickname: this.ownerUser?.nickname,
      gender: this.ownerUser?.gender,
      dateOfBirth: this.ownerUser?.dateOfBirth?.format('YYYY-MM-DD'),
      citizenId: this.citizenId,
      phone: this.contactPhone,
      email: this.contactEmail,
      interestedPackages: this.interestedPackages,
      referral: this.sourceChannel,
      referralOther: this.sourceChannelOther || '',
      referralMemberId: this.leadReferral?.[0]?.referralMemberId || 0,
      campaignId: this.campaignId ?? null,
      saleId: this.saleId,
    }
  }

  public serializeToUpdateProfile() {
    return {
      firstName: this.ownerUser?.firstName,
      lastName: this.ownerUser?.lastName,
      nickname: this.ownerUser?.nickname,
      gender: this.ownerUser?.gender,
      dateOfBirth: this.ownerUser?.dateOfBirth?.format('YYYY-MM-DD'),
      citizenId: this.citizenId,
    }
  }

  public serializeToUpdateContact() {
    return {
      phone: this.contactPhone,
      email: this.contactEmail,
    }
  }

  public serializeToUpdateReferral() {
    return {
      referral: this.sourceChannel,
      referralOther: this.sourceChannelOther || '',
      referralMemberId: this.leadReferral?.[0]?.referralMemberId || 0,
      campaignId: this.campaignId ?? null,
    }
  }

  public serializeToUpdateStatus() {
    return {
      leadStatus: this.status,
      reason: this.note,
    }
  }

  public serializeToUpdatePackage() {
    if (
      this.opportunity === undefined ||
      this.closeValueAmount === undefined ||
      this.closedAt === undefined
    ) {
      return {
        interestedPackages: this.interestedPackages,
      }
    }

    return {
      interestedPackages: this.interestedPackages,
      opportunity: this.opportunity,
      expectedCloseValue: this.closeValueAmount,
      expectedCloseDate: this.closedAt?.toISOString(),
    }
  }
}

export interface ToLeadResponse {
  success: boolean
  toStatus: string
}
